const OCCUPANCY_WEIGHT = 0.5;
const DENSITY_WEIGHT = 0.3;
const BURST_WEIGHT = 0.2;

const OCCUPANCY_RESOLUTION_MS = 200;
const DENSITY_BUCKET_MS = 30000;
const BURST_BUCKET_MS = 30000;

const OFFSET_SEARCH_RANGE_MS = 60000;

const timeToMs = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(/[:,.]/);
  if (parts.length >= 3) {
    return (
      parseInt(parts[0]) * 3600000 +
      parseInt(parts[1]) * 60000 +
      parseInt(parts[2]) * 1000 +
      (parts[3] ? parseInt(parts[3]) : 0)
    );
  }
  return 0;
};

const getTotalDurationMs = (cues) => {
  let maxEnd = 0;
  for (const cue of cues) {
    const end = timeToMs(cue.endTime);
    if (end > maxEnd) maxEnd = end;
  }
  return maxEnd || 600000;
};

const buildOccupancyTimeline = (cues, durationMs) => {
  const len = Math.ceil(durationMs / OCCUPANCY_RESOLUTION_MS);
  const timeline = new Int8Array(len);
  for (const cue of cues) {
    const start = Math.max(0, Math.floor(timeToMs(cue.startTime) / OCCUPANCY_RESOLUTION_MS));
    const end = Math.min(len - 1, Math.floor(timeToMs(cue.endTime) / OCCUPANCY_RESOLUTION_MS));
    for (let i = start; i <= end; i++) {
      timeline[i] = 1;
    }
  }
  return timeline;
};

const buildDensityVector = (cues, durationMs) => {
  const bucketCount = Math.ceil(durationMs / DENSITY_BUCKET_MS);
  const vec = new Float32Array(bucketCount);
  for (const cue of cues) {
    const startMs = timeToMs(cue.startTime);
    const endMs = timeToMs(cue.endTime);
    const startBucket = Math.floor(startMs / DENSITY_BUCKET_MS);
    const endBucket = Math.floor(endMs / DENSITY_BUCKET_MS);
    for (let b = startBucket; b <= endBucket && b < bucketCount; b++) {
      const bucketStart = b * DENSITY_BUCKET_MS;
      const bucketEnd = Math.min((b + 1) * DENSITY_BUCKET_MS, durationMs);
      const overlap = Math.max(0, Math.min(endMs, bucketEnd) - Math.max(startMs, bucketStart));
      vec[b] += overlap;
    }
  }
  return vec;
};

const buildBurstVector = (cues, durationMs) => {
  const bucketCount = Math.ceil(durationMs / BURST_BUCKET_MS);
  const vec = new Int32Array(bucketCount);
  for (const cue of cues) {
    const startMs = timeToMs(cue.startTime);
    const bucket = Math.floor(startMs / BURST_BUCKET_MS);
    if (bucket < bucketCount) vec[bucket]++;
  }
  return vec;
};

const align = (refTimeline, candTimeline, durationMs) => {
  let bestScore = -Infinity;
  let bestOffset = 0;
  let bestScale = 1;

  const candLen = candTimeline.length;
  const candDurationMs = candLen * OCCUPANCY_RESOLUTION_MS;

  const scales = [1, 0.97, 1.03, 0.95, 1.05];
  const offsets = [];
  for (let o = -OFFSET_SEARCH_RANGE_MS; o <= OFFSET_SEARCH_RANGE_MS; o += 2000) {
    offsets.push(o);
  }

  for (const scale of scales) {
    for (const offsetMs of offsets) {
      let score = 0;
      let overlap = 0;
      const step = OCCUPANCY_RESOLUTION_MS;

      for (let t = 0; t < durationMs; t += step) {
        const refIdx = Math.floor(t / step);
        if (refIdx >= refTimeline.length) break;
        if (refTimeline[refIdx] === 0) continue;

        const candT = t * scale + offsetMs;
        if (candT < 0 || candT >= candDurationMs) continue;

        const candIdx = Math.floor(candT / step);
        if (candIdx < candTimeline.length && candTimeline[candIdx] === 1) {
          score++;
        }
        overlap++;
      }

      if (overlap > 0) {
        const iou = overlap > 0 ? score / overlap : 0;
        if (iou > bestScore) {
          bestScore = iou;
          bestOffset = offsetMs;
          bestScale = scale;
        }
      }
    }
  }

  return { bestOffset, bestScale, bestIoU: bestScore };
};

const pearsonCorr = (a, b) => {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;

  let sumA = 0, sumB = 0, sumAB = 0, sumA2 = 0, sumB2 = 0;
  for (let i = 0; i < n; i++) {
    sumA += a[i];
    sumB += b[i];
    sumAB += a[i] * b[i];
    sumA2 += a[i] * a[i];
    sumB2 += b[i] * b[i];
  }

  const denom = Math.sqrt((n * sumA2 - sumA * sumA) * (n * sumB2 - sumB * sumB));
  if (denom === 0) return 0;
  return (n * sumAB - sumA * sumB) / denom;
};

const rankSubtitles = (refCues, candidates) => {
  const refDuration = getTotalDurationMs(refCues);

  const refOccupancy = buildOccupancyTimeline(refCues, refDuration);
  const refDensity = buildDensityVector(refCues, refDuration);
  const refBurst = buildBurstVector(refCues, refDuration);

  const results = [];

  for (const candidate of candidates) {
    const cues = candidate.cues || [];
    if (!cues || cues.length === 0) {
      results.push({
        subtitle: candidate,
        finalScore: 0,
        detectedOffsetMs: 0,
        detectedScaleFactor: 1,
        driftMsPerMinute: 0,
        occupancyScore: 0,
        densityScore: 0,
        burstScore: 0,
      });
      continue;
    }

    const candDuration = getTotalDurationMs(cues);
    const candOccupancy = buildOccupancyTimeline(cues, candDuration);
    const candDensity = buildDensityVector(cues, candDuration);
    const candBurst = buildBurstVector(cues, candDuration);

    const alignment = align(refOccupancy, candOccupancy, refDuration);
    const { bestOffset, bestScale } = alignment;

    let occupancyScore = alignment.bestIoU;
    if (occupancyScore < 0.1) occupancyScore *= 0.5;

    const shiftSamples = Math.round(bestOffset / OCCUPANCY_RESOLUTION_MS);
    let alignedDensity;
    if (shiftSamples >= 0) {
      alignedDensity = candDensity.slice(shiftSamples);
    } else {
      alignedDensity = new Float32Array(candDensity.length + Math.abs(shiftSamples));
      alignedDensity.set(new Float32Array(candDensity.length));
      for (let i = 0; i < candDensity.length; i++) {
        alignedDensity[i - shiftSamples] = candDensity[i];
      }
    }

    const densityCorr = pearsonCorr(refDensity, alignedDensity);

    let alignedBurst;
    if (shiftSamples >= 0) {
      alignedBurst = candBurst.slice(Math.floor(shiftSamples * OCCUPANCY_RESOLUTION_MS / BURST_BUCKET_MS));
    } else {
      const shiftBuckets = Math.ceil(Math.abs(shiftSamples) * OCCUPANCY_RESOLUTION_MS / BURST_BUCKET_MS);
      alignedBurst = new Int32Array(candBurst.length + shiftBuckets);
      for (let i = 0; i < candBurst.length; i++) {
        alignedBurst[i + shiftBuckets] = candBurst[i];
      }
    }

    const refBurstArr = Array.from(refBurst);
    const alignedBurstArr = Array.from(alignedBurst);
    const burstCorr = pearsonCorr(refBurstArr, alignedBurstArr);

    const finalScore = OCCUPANCY_WEIGHT * occupancyScore + DENSITY_WEIGHT * Math.max(0, densityCorr) + BURST_WEIGHT * Math.max(0, burstCorr);

    const driftMsPerMinute = bestScale !== 1 ? Math.abs(1 - bestScale) * 60000 : 0;

    results.push({
      subtitle: candidate,
      finalScore: Math.round(finalScore * 10000) / 10000,
      detectedOffsetMs: bestOffset,
      detectedScaleFactor: Math.round(bestScale * 10000) / 10000,
      driftMsPerMinute: Math.round(driftMsPerMinute * 100) / 100,
      occupancyScore: Math.round(occupancyScore * 10000) / 10000,
      densityScore: Math.round(Math.max(0, densityCorr) * 10000) / 10000,
      burstScore: Math.round(Math.max(0, burstCorr) * 10000) / 10000,
    });
  }

  results.sort((a, b) => b.finalScore - a.finalScore);
  results.forEach((r, i) => { r.rank = i + 1; });

  return results;
};

export { rankSubtitles, timeToMs };
